/**
 * Head-to-Head challenge logic — Supabase backend.
 */

import { supabase } from "./supabase";
import { isInIsrael } from "./geo";

export interface H2HChallenge {
  id: string;
  code: string;
  challenger_id: string;
  opponent_id: string | null;
  status: "pending" | "active" | "completed";
  song_ids: string[];
  created_at: string;
}

export interface H2HSong {
  song_id: string;
  title: string;
  artist: string;
  title_he?: string | null;
  artist_he?: string | null;
  title_en?: string | null;
  artist_en?: string | null;
}

/** Generate a 6-char invite code from a strictly unambiguous alphabet.
 *  Letters only — no digits at all, so 0/O, 1/I, 8/B, 5/S, 2/Z confusions
 *  can't happen even when read from a screenshot. Further excludes B, I, L,
 *  O, S, Z which are themselves ambiguous with each other or with digits
 *  that users might think they see. 20^6 = 64M codes, plenty for scale. */
function generateCode(): string {
  const alphabet = "ACDEFGHJKMNPQRTUVWXY";
  let out = "";
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/** Create a new H2H challenge with configurable song count and optional playlist */
export async function createChallenge(options: {
  songCount?: 1 | 3 | 5;
  playlistId?: string;
} = {}): Promise<{ code: string; id: string; songs: H2HSong[] } | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const songCount = options.songCount ?? 3;
  const songs: H2HSong[] = [];
  const usedIds: string[] = [];

  if (options.playlistId) {
    const { data: playlistSongs } = await supabase
      .from("playlist_songs")
      .select("song_id, songs(id, title, artist, title_he, artist_he)")
      .eq("playlist_id", options.playlistId);

    if (!playlistSongs?.length) return null;

    const shuffled = playlistSongs
      .filter((ps: any) => ps.songs)
      .sort(() => Math.random() - 0.5)
      .slice(0, songCount);

    for (const ps of shuffled) {
      const s = (ps as any).songs;
      songs.push({ song_id: s.id, title: s.title, artist: s.artist, title_he: s.title_he ?? null, artist_he: s.artist_he ?? null });
    }
  } else {
    const includeIsraeli = await isInIsrael();
    for (let attempt = 0; attempt < songCount + 5 && songs.length < songCount; attempt++) {
      const { data, error } = await supabase.rpc("get_random_song", {
        exclude_ids: usedIds,
        include_israeli: includeIsraeli,
      });
      if (error || !data?.[0]) continue;
      const songId = data[0].song_id;
      songs.push({ song_id: songId, title: data[0].title, artist: data[0].artist });
      usedIds.push(songId);
    }
  }

  if (songs.length === 0) return null;

  const code = generateCode();

  const { data: challenge, error } = await supabase
    .from("h2h_challenges")
    .insert({
      challenger_id: user.id,
      code,
      mode: options.playlistId ? "playlist" : "endless",
      playlist_id: options.playlistId || null,
      song_count: songCount,
      song_ids: songs,
      status: "pending",
    })
    .select("id")
    .maybeSingle();

  if (error || !challenge) {
    console.error("H2H insert failed:", error?.message);
    return null;
  }

  return { code, id: challenge.id, songs };
}

export type H2HStatus = "pending" | "active" | "completed";
export type H2HRole = "challenger" | "opponent" | "guest";

export interface H2HScoreEntry {
  song_index: number;
  stage: number;
  score: number;
}

export interface H2HLiveState {
  id: string;
  status: H2HStatus;
  challenger_id: string;
  opponent_id: string | null;
  challenger_completed_at: string | null;
  opponent_completed_at: string | null;
  guest_completed_at: string | null;
  challenger_scores: H2HScoreEntry[];
  opponent_scores: H2HScoreEntry[];
  guest_scores: H2HScoreEntry[];
}

/** Join (or resume) a challenge — uses SECURITY DEFINER RPC so authed
 *  opponents and guests can both flip pending→active reliably. */
export async function joinChallenge(code: string): Promise<{
  id: string;
  songs: H2HSong[];
  isSelf?: boolean;
  status?: H2HStatus;
} | null> {
  const { data, error } = await supabase.rpc("h2h_join", {
    p_code: code.toUpperCase(),
  });
  if (error) {
    console.error("h2h_join failed:", error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  const songs = row.song_ids as H2HSong[];
  if (!songs?.length) return null;
  return {
    id: row.id,
    songs,
    isSelf: row.is_self === true,
    status: row.status as H2HStatus,
  };
}

/** Minimal "is the opponent here yet?" probe for the waiting screen. */
export async function getChallengeStatus(code: string): Promise<{
  id: string;
  status: H2HStatus;
} | null> {
  const { data } = await supabase
    .from("h2h_challenges")
    .select("id, status")
    .eq("code", code.toUpperCase())
    .maybeSingle();
  return data ? { id: data.id, status: data.status as H2HStatus } : null;
}

/** Subscribe to realtime updates for a challenge. Fires `onChange` whenever
 *  the h2h_challenges row or any linked h2h_scores row changes. Returns an
 *  unsubscribe function.
 *
 *  Caller is expected to fetch the latest full state via `getH2HLiveState`
 *  inside `onChange` — we intentionally don't pass the payload through,
 *  because realtime events only include the single changed row, not the
 *  joined view (scores from both sides + guest_scores) that the UI needs.
 *
 *  The caller should keep a low-frequency polling fallback (e.g. every
 *  15–30s) in case the websocket drops without firing an error. */
export function subscribeToH2H(h2hId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`h2h:${h2hId}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "h2h_challenges", filter: `id=eq.${h2hId}` },
      () => onChange()
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "h2h_scores", filter: `h2h_id=eq.${h2hId}` },
      () => onChange()
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

/** Full live state for polling during play / final results. */
export async function getH2HLiveState(code: string): Promise<H2HLiveState | null> {
  const { data, error } = await supabase.rpc("h2h_get_state", {
    p_code: code.toUpperCase(),
  });
  if (error) {
    console.error("h2h_get_state failed:", error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    id: row.id,
    status: row.status as H2HStatus,
    challenger_id: row.challenger_id,
    opponent_id: row.opponent_id,
    challenger_completed_at: row.challenger_completed_at,
    opponent_completed_at: row.opponent_completed_at,
    guest_completed_at: row.guest_completed_at,
    challenger_scores: (row.challenger_scores ?? []) as H2HScoreEntry[],
    opponent_scores: (row.opponent_scores ?? []) as H2HScoreEntry[],
    guest_scores: (row.guest_scores ?? []) as H2HScoreEntry[],
  };
}

/** Save score for one song — persists for both authed users AND guests. */
export async function saveH2HScore(h2hId: string, songIndex: number, stage: number, score: number): Promise<void> {
  const { error } = await supabase.rpc("h2h_save_score", {
    p_h2h_id: h2hId,
    p_song_index: songIndex,
    p_stage: stage,
    p_score: score,
  });
  if (error) console.error("h2h_save_score failed:", error.message);
}

/** Pick the right score totals for the current player's perspective. */
export function rollupH2HTotals(state: H2HLiveState, role: H2HRole): {
  myTotal: number;
  opponentTotal: number;
  opponentFinished: boolean;
  opponentIsGuest: boolean;
} {
  const sum = (xs: H2HScoreEntry[]) => xs.reduce((a, b) => a + (b.score || 0), 0);
  const guestPresent = state.guest_scores.length > 0 || !!state.guest_completed_at;
  const opponentIsGuest = role === "challenger" && !state.opponent_id && guestPresent;

  if (role === "challenger") {
    const oppScores = opponentIsGuest ? state.guest_scores : state.opponent_scores;
    return {
      myTotal: sum(state.challenger_scores),
      opponentTotal: sum(oppScores),
      opponentFinished: opponentIsGuest
        ? !!state.guest_completed_at
        : !!state.opponent_completed_at,
      opponentIsGuest,
    };
  }
  // Opponent or guest: "my side" is the opponent/guest slot; opponent = challenger.
  const myScores = role === "guest" ? state.guest_scores : state.opponent_scores;
  return {
    myTotal: sum(myScores),
    opponentTotal: sum(state.challenger_scores),
    opponentFinished: !!state.challenger_completed_at,
    opponentIsGuest: false,
  };
}

/** Back-compat: still used by final-results rendering before the first poll. */
export async function getH2HResults(h2hId: string): Promise<{
  myTotal: number;
  opponentTotal: number;
  myScores: { stage: number; score: number }[];
  opponentScores: { stage: number; score: number }[];
} | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: scores } = await supabase
    .from("h2h_scores")
    .select("user_id, song_index, stage, score")
    .eq("h2h_id", h2hId)
    .order("song_index");

  if (!scores) return null;

  const myScores = scores
    .filter((s) => s.user_id === user.id)
    .sort((a, b) => a.song_index - b.song_index)
    .map((s) => ({ stage: s.stage, score: s.score }));

  const opponentScores = scores
    .filter((s) => s.user_id !== user.id)
    .sort((a, b) => a.song_index - b.song_index)
    .map((s) => ({ stage: s.stage, score: s.score }));

  return {
    myTotal: myScores.reduce((sum, s) => sum + s.score, 0),
    opponentTotal: opponentScores.reduce((sum, s) => sum + s.score, 0),
    myScores,
    opponentScores,
  };
}

/** Mark the caller's side finished — flips to "completed" only when BOTH done. */
export async function completeChallenge(h2hId: string): Promise<void> {
  const { error } = await supabase.rpc("h2h_complete", { p_h2h_id: h2hId });
  if (error) console.error("h2h_complete failed:", error.message);
}

/** Fire a server-side web push to the OTHER participant of this H2H
 *  announcing that the current user just finished. Non-blocking — failure
 *  is logged but doesn't propagate. Gated by VITE_H2H_PUSH_ENABLED so the
 *  whole feature can be toggled off via env without a code change. */
export async function notifyOpponentOfFinish(h2hId: string): Promise<void> {
  if (import.meta.env.VITE_H2H_PUSH_ENABLED === "false") return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return; // guest — can't auth to the function anyway
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-h2h-opponent-finished`;
    // Fire-and-forget: we don't await the response because the user is
    // already on the final-results screen and we don't want UI latency.
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ h2h_id: h2hId }),
    }).catch((e) => console.error("notifyOpponentOfFinish fetch failed:", e));
  } catch (e) {
    console.error("notifyOpponentOfFinish error:", e);
  }
}

/** Delete a challenge (pending, active, or completed).
 *  Scores are deleted implicitly via h2h_scores.h2h_id ON DELETE CASCADE,
 *  so we only need to delete the parent row. Before migration 015 the
 *  DELETE silently failed due to a missing RLS policy — errors are now
 *  logged so any future regression surfaces. */
export async function deleteChallenge(h2hId: string): Promise<void> {
  const { error } = await supabase.from("h2h_challenges").delete().eq("id", h2hId);
  if (error) console.error("deleteChallenge failed:", error.message);
}

/** Load the song list for a challenge. Used when opening a previously
 *  created/joined challenge from the "recent duels" list, so we can
 *  populate state before jumping into the results view. */
export async function getChallengeSongs(h2hId: string): Promise<H2HSong[] | null> {
  const { data, error } = await supabase
    .from("h2h_challenges")
    .select("song_ids")
    .eq("id", h2hId)
    .maybeSingle();
  if (error || !data) return null;
  return (data.song_ids as H2HSong[]) ?? null;
}

/** Get my recent challenges */
export async function getRecentChallenges(): Promise<{
  id: string;
  code: string;
  status: string;
  is_challenger: boolean;
  created_at: string;
  song_count: number;
}[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data } = await supabase
    .from("h2h_challenges")
    .select("id, code, status, challenger_id, created_at, song_count")
    .or(`challenger_id.eq.${user.id},opponent_id.eq.${user.id}`)
    .order("created_at", { ascending: false })
    .limit(10);

  return (data ?? []).map((c) => ({
    id: c.id,
    code: c.code,
    status: c.status,
    is_challenger: c.challenger_id === user.id,
    created_at: c.created_at,
    song_count: c.song_count ?? 3,
  }));
}
